import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Menu, X, Home, Info, Phone, Briefcase, Users, Bell, ChevronDown, LogOut, Settings, User } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import UserDropdownMenu from '@/components/UserDropdownMenu';

const LoggedInHeader = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const currentPath = location.pathname;
  
  // Main navigation items - same as public but Home goes to Dashboard
  const mainNavItems = [
    { name: 'Home', path: '/dashboard', icon: Home },
    { name: 'About', path: '/about', icon: Info },
    { name: 'Contact', path: '/contact', icon: Phone },
    { name: 'Services', path: '/services', icon: Briefcase },
    { name: 'Workshops', path: '/workshops', icon: Users },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return currentPath === '/dashboard' || currentPath === '/';
    }
    return currentPath.startsWith(path);
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center py-1">
          {/* Left side: Back button + Logo */}
          <div className="flex items-center space-x-3">
            {/* Back Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="hover:bg-sky-50 text-gray-600"
              data-testid="logged-in-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            
            {/* Logo - Large size matching landing page */}
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate('/dashboard')}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_role-expiry-dash/artifacts/swo6wssk_career_support_80px_transparent_EXACT.png" 
                alt="Career Support" 
                className="h-32 sm:h-36 w-auto -my-8"
              />
            </div>
          </div>

          {/* Center: Main Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {mainNavItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                onClick={() => navigate(item.path)}
                className={`px-4 py-2 ${
                  isActive(item.path)
                    ? 'text-sky-600 font-semibold bg-sky-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                data-testid={`logged-in-nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Button>
            ))}
          </div>

          {/* Right side: User menu */}
          <div className="flex items-center space-x-3">
            {/* Notifications */}
            <NotificationBell />
            
            {/* User Dropdown */}
            <UserDropdownMenu user={user} onLogout={onLogout} />
            
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="logged-in-mobile-menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  className={`w-full justify-start ${
                    isActive(item.path)
                      ? 'text-sky-600 font-semibold bg-sky-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Button>
              );
            })}
            <div className="border-t border-gray-200 pt-3 mt-3">
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                onClick={() => {
                  navigate('/settings');
                  setMobileMenuOpen(false);
                }}
              >
                <Settings className="w-5 h-5 mr-3" />
                Settings
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => {
                  if (onLogout) onLogout();
                  setMobileMenuOpen(false);
                }}
              >
                <LogOut className="w-5 h-5 mr-3" />
                Log Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LoggedInHeader;
